import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { HelpCircle, Search, Plus, Minus, Zap, ShieldCheck, Target, Award, MapPin } from 'lucide-react';

export default function FAQ() {
  const [activeTab, setActiveTab] = useState(0);
  const [openQuestionIndex, setOpenQuestionIndex] = useState(0); // Default open Q1 of active tab
  const [searchQuery, setSearchQuery] = useState('');

  // Categorized FAQ Data matching Screenshot 1
  const categories = [
    {
      id: 0,
      title: "ABOUT ACADEMY",
      icon: Target,
      questions: [
        {
          q: "What is Chaitanya Shooting Academy known for?",
          a: "Chaitanya Shooting Academy is Sector 81 Gurugram's premier precision sports center. We are known for Olympic-standard 10m Air Rifle and Pistol lanes, NRAI-certified expert coaching, and nurturing state and national level marksmen."
        },
        {
          q: "Who is the Head Coach of Chaitanya Shooting Academy?",
          a: "The academy is founded and guided by Head Coach Vikash Yadav, an NRAI-certified technical shooting coach with years of national competitive experience and a track record of training medal-winning shooters."
        },
        {
          q: "What makes Chaitanya Shooting Academy different from other shooting ranges?",
          a: "We combine SIUS electronic precision targets with individualized 1-on-1 coaching, mental focus conditioning, and 100% safety protocols. Plus, all rifles, pellets, and protective equipment are included with no hidden fees."
        },
        {
          q: "What is the minimum age to join the academy?",
          a: "We welcome shooters from age 7 onwards! For young juniors (7-12 years), we have specialized lightweight training equipment and modified posture conditioning routines."
        }
      ]
    },
    {
      id: 1,
      title: "TRAINING & FACILITY",
      icon: ShieldCheck,
      questions: [
        {
          q: "What equipment do I need for my first session?",
          a: "Zero equipment required! We provide high-precision Olympic-standard air rifles/pistols, pellets, shooting jackets, safety glasses, and ear protection for all our students."
        },
        {
          q: "What are the range specifications and target systems?",
          a: "Our indoor range features 15 Olympic-standard 10m lanes equipped with SIUS electronic target systems, automated target retrievers, and climate-controlled lighting designed for maximum focus."
        },
        {
          q: "What training programs do you offer?",
          a: "We offer Beginner Courses (3 Months - Fundamentals), Intermediate Training (6 Months - Match Preparation), Professional Athlete Program (12+ Months - National Competition Prep), and Weekend Youth Batches."
        },
        {
          q: "How many training sessions should a beginner attend weekly?",
          a: "We recommend 2 to 3 sessions per week for beginners (1.5 hours per session). For competitive athletes, 4 to 5 sessions per week are recommended."
        }
      ]
    },
    {
      id: 2,
      title: "ADMISSION & LOCATION",
      icon: MapPin,
      questions: [
        {
          q: "Where is Chaitanya Shooting Academy located?",
          a: "We are located near Vipul Lavanya Society, Sector 81, Gurugram, Haryana 122050. Conveniently accessible from NH-48, Dwarka Expressway, and New Gurgaon sectors."
        },
        {
          q: "How do I book a Free Trial Session?",
          a: "Click on 'Schedule Trial' or fill out our 4-Step Shooter Registration Form on our website. You can pick your preferred date and time slot (Tue - Sun), and our coaches will confirm your lane."
        },
        {
          q: "What are the academy operating hours?",
          a: "We are open Tuesday through Sunday from 8:00 AM to 8:00 PM. Please note that the academy remains closed on Mondays for maintenance and range calibration."
        },
        {
          q: "Are there monthly and annual membership options?",
          a: "Yes! We offer flexible monthly, quarterly, half-yearly, and annual membership plans with multi-month discounts and family batch offers."
        }
      ]
    },
    {
      id: 3,
      title: "GENERAL QUERIES",
      icon: Award,
      questions: [
        {
          q: "Do I need a gun license to train at the range?",
          a: "No gun license is required! We train using .177 caliber (4.5mm) Olympic air rifles and air pistols, which do not require a arms license in India under Ministry of Youth Affairs & Sports regulations."
        },
        {
          q: "Will I get certificates and opportunities to compete in championships?",
          a: "Yes! Students receive progress certificates recognized by state associations. We guide eligible shooters through District, State, All India KSS, and NRAI National Qualification trials."
        },
        {
          q: "Can I bring my own rifle or equipment?",
          a: "Intermediate and advanced shooters can bring their personal NRAI-compliant equipment. Our technical coaches will inspect and assist with sight alignment and rifle fitting."
        },
        {
          q: "What is your refund or session pause policy?",
          a: "If you need to take a break due to exams or travel, you can freeze your membership for up to 30 days without losing any remaining sessions."
        }
      ]
    }
  ];

  // Filtering questions by Search query across all categories
  const currentCategory = categories[activeTab];

  const getFilteredQuestions = () => {
    if (!searchQuery.trim()) return currentCategory.questions;
    const query = searchQuery.toLowerCase();
    
    // Search across current active tab or all tabs
    return currentCategory.questions.filter(
      item => item.q.toLowerCase().includes(query) || item.a.toLowerCase().includes(query)
    );
  };

  const filteredQuestions = getFilteredQuestions();

  return (
    <section id="faq" className="py-24 md:py-32 bg-[#050505] px-4 sm:px-6 relative overflow-hidden">
      {/* Glow Effects */}
      <div className="absolute top-1/3 left-0 w-96 h-96 bg-red-600/5 blur-[130px] rounded-full -z-10" />
      <div className="absolute bottom-10 right-0 w-96 h-96 bg-gold/5 blur-[130px] rounded-full -z-10" />

      <div className="max-w-5xl mx-auto">
        
        {/* HEADER */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12 sm:mb-16"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-red-600/10 border border-red-600/20 mb-4">
            <HelpCircle size={14} className="text-red-500 animate-pulse" />
            <span className="text-[10px] font-black tracking-[0.35em] uppercase text-red-500">Knowledge Base</span>
          </div>

          <h2 className="font-heading text-3xl sm:text-5xl md:text-6xl font-black italic uppercase leading-[0.9] text-white tracking-tighter mb-4">
            FAQs (<span className="text-gold">Frequently Asked Questions</span>)
          </h2>

          <p className="text-zinc-400 text-sm sm:text-base max-w-xl mx-auto font-medium">
            Everything you need to know about admissions, coaching programs, range safety, and equipment.
          </p>

          {/* SEARCH BOX */}
          <div className="mt-8 max-w-md mx-auto relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500" size={18} />
            <input
              type="text"
              placeholder="Search questions (e.g. age, fee, location, trial)..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-zinc-900/90 border border-white/10 pl-12 pr-4 py-3.5 rounded-2xl text-white text-xs font-bold outline-none focus:border-gold transition-all placeholder:text-zinc-600 shadow-xl"
            />
          </div>
        </motion.div>

        {/* --- CATEGORY TAB NAVIGATION (Pill Tabs) --- */}
        <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3 mb-10">
          {categories.map((cat) => {
            const isActive = activeTab === cat.id;

            return (
              <button
                key={cat.id}
                onClick={() => {
                  setActiveTab(cat.id);
                  setOpenQuestionIndex(0); // Reset to first Q when tab changes
                }}
                className={`relative px-5 py-3 rounded-full text-xs font-black uppercase tracking-wider transition-all duration-300 ${
                  isActive
                    ? 'bg-gradient-to-r from-red-600 to-red-700 text-white shadow-[0_0_25px_rgba(220,38,38,0.5)] scale-105 ring-2 ring-red-600/50'
                    : 'bg-white/5 text-zinc-300 border border-white/10 hover:bg-white/10 hover:border-gold/50'
                }`}
              >
                {cat.title}
              </button>
            );
          })}
        </div>

        {/* --- MAIN QUESTIONS ACCORDION CONTAINER --- */}
        <div className="bg-gradient-to-br from-zinc-900/90 via-black to-zinc-950 border border-gold/30 p-4 sm:p-8 rounded-[2.5rem] shadow-2xl backdrop-blur-xl">
          
          {filteredQuestions.length === 0 ? (
            <div className="text-center py-12 text-zinc-400 font-bold text-sm">
              No matching questions found for "{searchQuery}". Try selecting another tab or clearing search.
            </div>
          ) : (
            <div className="space-y-3.5">
              {filteredQuestions.map((item, idx) => {
                const isOpen = openQuestionIndex === idx;

                return (
                  <div 
                    key={idx}
                    className={`rounded-2xl transition-all duration-300 border ${
                      isOpen 
                        ? 'bg-zinc-900/90 border-gold shadow-[0_0_20px_rgba(255,215,0,0.15)]' 
                        : 'bg-black/60 border-gold/30 hover:border-gold/70'
                    }`}
                  >
                    {/* QUESTION HEADER BUTTON */}
                    <button
                      type="button"
                      onClick={() => setOpenQuestionIndex(isOpen ? null : idx)}
                      className="w-full text-left p-4 sm:p-6 flex items-center justify-between gap-4 outline-none"
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-gold font-black text-sm sm:text-base font-heading">
                          Q{idx + 1}.
                        </span>
                        <h4 className="text-white font-bold text-sm sm:text-base leading-snug">
                          {item.q}
                        </h4>
                      </div>

                      <div className={`w-8 h-8 rounded-full border flex items-center justify-center shrink-0 transition-all ${
                        isOpen ? 'bg-gold text-black border-gold' : 'border-gold/50 text-gold hover:bg-gold/10'
                      }`}>
                        {isOpen ? <Minus size={16} strokeWidth={3} /> : <Plus size={16} strokeWidth={3} />}
                      </div>
                    </button>

                    {/* ANSWER ACCORDION EXPANSION */}
                    <AnimatePresence>
                      {isOpen && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.25, ease: "easeInOut" }}
                          className="overflow-hidden"
                        >
                          <div className="px-4 sm:px-6 pb-5 pt-1 text-zinc-300 text-xs sm:text-sm leading-relaxed border-t border-white/5 mt-1 font-medium">
                            {item.a}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}
            </div>
          )}

          {/* BOTTOM DIRECT HELP BANNER */}
          <div className="mt-8 pt-6 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4 bg-white/5 p-4 sm:p-6 rounded-2xl">
            <div>
              <p className="text-white font-bold text-xs uppercase tracking-wider">Have a specific question not listed here?</p>
              <p className="text-zinc-400 text-xs mt-0.5">Reach out directly to Coach Vikash Yadav & team</p>
            </div>
            <div className="flex items-center gap-3 w-full sm:w-auto">
              <a 
                href="tel:+919653524055"
                className="flex-1 sm:flex-initial bg-red-600 hover:bg-red-700 text-white px-5 py-2.5 rounded-xl font-bold text-xs uppercase tracking-wider text-center"
              >
                📞 Call Now
              </a>
              <a 
                href="https://wa.me/919653524055?text=Hi%20Coach,%20I%20have%20a%20question%20about%20shooting%20training."
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 sm:flex-initial bg-[#25D366] hover:bg-[#20bd5a] text-white px-5 py-2.5 rounded-xl font-bold text-xs uppercase tracking-wider text-center"
              >
                💬 WhatsApp
              </a>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}
